import { Link } from "react-router-dom";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { FadeUp } from "@/components/Reveal";
import { PROCESS } from "@/data/content";

export default function HowItWorks() {
    return (
        <>
            <Seo
                title="How It Works — The Five-Step Process | Dagaz Development"
                description="Audit, concept and budget, design and permits, managed build with licensed local builders, documented handover. The Dagaz Development process, step by step."
                path="/how-it-works"
                jsonLd={[
                    orgSchema,
                    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "How It Works", path: "/how-it-works" }]),
                    {
                        "@context": "https://schema.org",
                        "@type": "HowTo",
                        name: "How a Dagaz Development project works",
                        step: PROCESS.map((s, i) => ({
                            "@type": "HowToStep",
                            position: i + 1,
                            name: s.title,
                            text: s.body,
                        })),
                    },
                ]}
            />
            <PageHeader
                eyebrow="Process — Five Steps"
                titleLines={["No mystery.", "No surprises."]}
                answer="Every Dagaz Development project follows the same five steps: a paid audit, concept with a written budget, design and permits, a managed build with licensed local builders, and a documented handover. You approve each step before the next begins."
                crumbs={[{ name: "How It Works" }]}
            />
            <section className="px-6 md:px-12 lg:px-16 pb-28 md:pb-40 bg-chalk" data-testid="process-steps">
                {PROCESS.map((step, i) => (
                    <FadeUp key={step.n} delay={0.04 * i}>
                        <div className="grid lg:grid-cols-12 gap-6 py-14 border-t border-graphite/15">
                            <div className="lg:col-span-2">
                                <span className="font-serif font-light text-7xl md:text-8xl text-graphite/10">{step.n}</span>
                            </div>
                            <div className="lg:col-span-4">
                                <h2 className="font-serif font-light text-3xl md:text-4xl text-graphite">{step.title}</h2>
                                <p className="mt-4 inline-block px-3 py-1.5 border border-gold/50 font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-graphite">
                                    {step.duration}
                                </p>
                            </div>
                            <div className="lg:col-span-5 lg:col-start-8">
                                <p className="font-sans text-base leading-relaxed text-graphite/75">{step.body}</p>
                            </div>
                        </div>
                    </FadeUp>
                ))}
                <div className="border-t border-graphite/15" />
                <FadeUp className="mt-16 flex flex-wrap items-center gap-8">
                    <p className="font-serif font-light text-2xl md:text-3xl text-graphite">Ready for step one?</p>
                    <Link
                        to="/start-a-project"
                        data-testid="process-cta-start-project"
                        className="group inline-flex items-center gap-3 px-8 py-4 bg-forest text-chalk font-sans text-xs font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-graphite"
                    >
                        Start a Project
                        <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                    </Link>
                </FadeUp>
            </section>
        </>
    );
}
